import { Typography } from '@material-ui/core';
import { Formik } from 'formik';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import useStyles from '@components/common/reset-password-form/styles';
import { UserSendResetPasswordLinkData } from '@lib/api';
import { clearEmailSent } from '@store/auth';
import { resetPassword, resettingPassword } from '@store/auth/thunks';
import Validation from '@utils/validation';

import Button from '../button';
import Field from '../field';
import FullscreenPopup from '../fullscreen-popup';

const resetPasswordValidation = Yup.object().shape({
  email: Validation.emailRequired,
});

const resettingPasswordValidation = Yup.object().shape({
  password: Validation.password,
});

interface Props {
  resettingEmailSent: string | undefined;
  passwordResetSuccess: string | undefined;
  userToken?: string;
  isLoading: boolean;
  emailToReset: string;
}

const ResetPasswordForm: React.FC<Props> = (props) => {
  const {
    resettingEmailSent,
    passwordResetSuccess,
    userToken,
    isLoading,
    emailToReset,
  } = props;

  const dispatch = useDispatch();
  const [requestSended, setRequestSended] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const classes = useStyles();

  useEffect(() => {
    if (resettingEmailSent) setRequestSended(true);
  }, [resettingEmailSent]);

  useEffect(() => {
    if (passwordResetSuccess) setShowPopup(true);
  }, [passwordResetSuccess]);

  useEffect(() => {
    return () => {
      dispatch(clearEmailSent());
    };
  });

  const onSubmitResetPassword = (data: UserSendResetPasswordLinkData) => {
    dispatch(resetPassword(data));
  };

  const onSubmitResettingPassword = (data: { password: string }) => {
    if (userToken) {
      const requestParameters = {
        password: data.password,
        token: userToken,
      };

      dispatch(resettingPassword(requestParameters));
    }
  };

  const resetPasswordForm = (
    <section>
      <section>
        <Typography
          component="h1"
          variant="subtitle1"
          className={classes.title}
        >
          Forgot your password?
        </Typography>
        <Typography variant="body2" className={classes.description}>
          Please enter your email address and we&apos;ll send you a link to
          reset your password
        </Typography>
      </section>
      <Formik
        initialValues={{
          email: '',
        }}
        onSubmit={onSubmitResetPassword}
        validationSchema={resetPasswordValidation}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <div>
              <Field
                id="email-reset-password"
                type="email"
                placeholder="Your email address"
                label="Your email address"
                variant="filled"
                name="email"
                value={values.email}
                onChange={handleChange}
                errorMessage={errors.email}
              />
            </div>

            <Button
              id="reset-password-button"
              type="submit"
              styleType="containedBlue"
              text="Reset"
              className={classes.submitButton}
              disabled={isLoading}
              isLoading={isLoading}
            />
          </form>
        )}
      </Formik>
    </section>
  );

  const emailWasSendedInfo = (
    <section>
      <Typography variant="subtitle1" className={classes.title}>
        Check your inbox
      </Typography>
      <Typography variant="body2" className={classes.description}>
        We just sent a link to reset your password via email to{' '}
        <b>{emailToReset}</b>
      </Typography>
      <Button
        id="back-to-main-page-reset-password"
        type="button"
        href="/"
        styleType="containedBlue"
        text="Back to Main page"
        className={classes.submitButton}
        onClick={() => setRequestSended(true)}
      />
    </section>
  );

  const resettingPasswordForm = (
    <section>
      <section>
        <Typography variant="subtitle1" className={classes.title}>
          Resetting your password
        </Typography>
        <Typography variant="body2" className={classes.description}>
          Please enter your new password
        </Typography>
      </section>
      <Formik
        initialValues={{
          password: '',
        }}
        onSubmit={onSubmitResettingPassword}
        validationSchema={resettingPasswordValidation}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <div>
              <Field
                id="password-resetting-password"
                type="password"
                placeholder="Password"
                label="Password"
                variant="filled"
                name="password"
                value={values.password}
                onChange={handleChange}
                errorMessage={errors.password}
                showHidePassword
              />
            </div>

            <Button
              id="reset-password-button-resetting-password"
              type="submit"
              styleType="containedBlue"
              text="Reset password"
              className={classes.submitButton}
              disabled={isLoading}
              isLoading={isLoading}
            />
          </form>
        )}
      </Formik>
    </section>
  );

  const pageData = () => {
    if (userToken) {
      return resettingPasswordForm;
    }

    if (requestSended) {
      return emailWasSendedInfo;
    }

    return resetPasswordForm;
  };

  return (
    <>
      {pageData()}
      {showPopup && (
        <FullscreenPopup
          type="showMessage"
          title="Your password was successfully set"
          description="Just log in with your new password"
          imageUrl="/png/lock.png"
          href="/sign-in"
          imageClass={classes.lockImage}
        />
      )}
    </>
  );
};

export default ResetPasswordForm;
